import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/** Marks we ship a real logo for. Everything else renders a monogram tile. */
export type BrandKey = 'atlassian' | 'salesforce' | 'microsoft' | 'slack' | 'github' | 'catsy';

/** Connector registry `name` → brand mark. Aliases cover the sibling products
 *  that share one vendor logo (Jira/Confluence are Atlassian; the Graph and
 *  meetings connectors are Microsoft), so a new connector on an existing
 *  vendor gets the right logo without a code change. */
const BRAND_ALIASES: Record<string, BrandKey> = {
  atlassian: 'atlassian', jira: 'atlassian', confluence: 'atlassian',
  salesforce: 'salesforce', sfdc: 'salesforce',
  microsoft: 'microsoft', 'microsoft-meetings': 'microsoft', 'microsoft-365': 'microsoft',
  msgraph: 'microsoft', graph: 'microsoft', outlook: 'microsoft', teams: 'microsoft',
  m365: 'microsoft', office365: 'microsoft',
  slack: 'slack',
  github: 'github',
  catsy: 'catsy',
};

/** Monogram tints — picked by a stable hash of the connector name so an
 *  unknown connector always gets the SAME colour, never a random one per
 *  render. Deliberately desaturated: a monogram must not out-shout a real
 *  brand mark sitting next to it in the list. */
const MONO_TINTS = ['#5b6b7f', '#6b5f7f', '#4f7068', '#7f6b52', '#4d6a86', '#7a5b63'];

/**
 * The vendor logo for a connector — the real mark (Atlassian, Salesforce,
 * Microsoft, Slack, GitHub) or a stable monogram tile when we don't ship one.
 *
 * Logos are INLINE SVG on purpose. The obvious alternative — a favicon service
 * like `google.com/s2/favicons?domain=…` — would send a request to a third
 * party for every connector row (telling them which integrations this tenant
 * runs), break under a strict CSP, and render nothing offline or on a slow
 * link. Inline marks are exact at any size, themable, and free.
 *
 * Colour handling: brand marks keep their real colours, so the caller sits
 * them on a light tile in BOTH themes (see `.cx-tile` in the connections
 * modal) rather than inverting them — an inverted Microsoft logo is not the
 * Microsoft logo.
 */
@Component({
  selector: 'app-brand-mark',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './brand-mark.component.html',
  styleUrls: ['./brand-mark.component.scss'],
})
export class BrandMarkComponent {
  /** Connector registry name, e.g. `atlassian`, `microsoft-meetings`. */
  readonly name = input.required<string>();
  /** Human name — only used to derive monogram initials. */
  readonly label = input<string>('');
  /** Rendered edge length in px. */
  readonly size = input<number>(20);

  /** Resolved mark, or null when we fall back to a monogram. Falls back to
   *  the first hyphen segment so vendor-suffixed connector names
   *  (`atlassian-mcp`, `salesforce-data360`, …) inherit the vendor's mark
   *  without a map entry — the promise the alias-map comment makes. */
  readonly key = computed<BrandKey | null>(() => {
    const name = (this.name() || '').trim().toLowerCase();
    return BRAND_ALIASES[name] ?? BRAND_ALIASES[name.split('-')[0]] ?? null;
  });

  /** Up to two letters: initials of the first two words, else the first two
   *  characters. `Microsoft 365 meetings` → `M3`, `Atlassian` → `AT`. */
  readonly mono = computed(() => {
    const src = (this.label() || this.name() || '?').trim();
    const words = src.split(/[\s_-]+/).filter(Boolean);
    const raw = words.length > 1 ? words[0][0] + words[1][0] : src.slice(0, 2);
    return raw.toUpperCase();
  });

  readonly monoTint = computed(() => {
    const src = (this.name() || this.label() || '').toLowerCase();
    let h = 0;
    for (let i = 0; i < src.length; i++) { h = (h * 31 + src.charCodeAt(i)) >>> 0; }
    return MONO_TINTS[h % MONO_TINTS.length];
  });

  readonly px = computed(() => `${this.size()}px`);
}
